import React from "react";
import { Button, HStack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import RiskReviewApprovalFilters from "../components/approvals/RiskReviewApprovalFilters";
import RiskReviewApprovalTable from "../components/approvals/RiskReviewApprovalTable";
import RiskReviewConfirmDialog from "../components/common/RiskReviewConfirmDialog";
import RiskReviewEmptyState from "../components/common/RiskReviewEmptyState";
import RiskReviewErrorState from "../components/common/RiskReviewErrorState";
import RiskReviewLoadingState from "../components/common/RiskReviewLoadingState";
import RiskReviewPageShell from "../components/common/RiskReviewPageShell";
import RiskReviewPanel from "../components/common/RiskReviewPanel";
import { riskReviewKeys } from "../constants/riskReviewQueryKeys";
import { useRiskReviewApprovals } from "../hooks/useRiskReviewApprovals";
import riskReviewService from "../services/riskReviewService";
import { getRiskReviewErrorMessage } from "../utils/riskReviewFormatters";

const RiskReviewApprovalsPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filters, setFilters] = React.useState({ status: "PENDING" });
  const [selected, setSelected] = React.useState([]);
  const [confirmBulk, setConfirmBulk] = React.useState(false);
  const query = useRiskReviewApprovals(filters);
  const approvals = Array.isArray(query.data) ? query.data : [];

  const bulkMutation = useMutation({
    mutationFn: riskReviewService.bulkApprove,
    onSuccess: (result) => {
      toast({ title: `${Array.isArray(result) ? result.length : 0} approved`, status: "success" });
      setSelected([]);
      setConfirmBulk(false);
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.all });
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  const onSelect = (id, checked) => {
    setSelected((current) => (checked ? [...current, id] : current.filter((item) => item !== id)));
  };

  return (
    <RiskReviewPageShell
      title="My Risk Review Approvals"
      description="Review and action requests assigned to you."
    >
      <RiskReviewPanel>
        <RiskReviewApprovalFilters filters={filters} onChange={setFilters} onReset={() => setFilters({ status: "PENDING" })} />
      </RiskReviewPanel>
      {selected.length ? (
        <RiskReviewPanel py={3}>
          <HStack justify="space-between" wrap="wrap">
            <Text fontWeight="medium">{selected.length} requests selected</Text>
            <HStack>
              <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
                Clear Selection
              </Button>
              <Button size="sm" colorScheme="green" onClick={() => setConfirmBulk(true)}>
                Bulk Approve
              </Button>
            </HStack>
          </HStack>
        </RiskReviewPanel>
      ) : null}
      {query.isLoading ? <RiskReviewLoadingState rows={8} /> : null}
      {query.isError ? <RiskReviewErrorState error={query.error} onRetry={query.refetch} /> : null}
      {query.isSuccess && approvals.length ? (
        <RiskReviewApprovalTable approvals={approvals} selected={selected} onSelect={onSelect} />
      ) : null}
      {query.isSuccess && !approvals.length ? (
        <RiskReviewEmptyState title="No approvals found" message="No Risk Reviews are assigned to you for the current filters." />
      ) : null}
      <RiskReviewConfirmDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        title={`Approve ${selected.length} Risk Reviews?`}
        body="Each request will still be validated independently by the backend."
        confirmLabel={`Approve ${selected.length} Requests`}
        colorScheme="green"
        isLoading={bulkMutation.isPending}
        onConfirm={() => bulkMutation.mutate({ approvalIds: selected })}
      />
    </RiskReviewPageShell>
  );
};

export default RiskReviewApprovalsPage;
